import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import DatePicker from 'react-datepicker';
import classNames from 'classnames';
import { format, startOfMonth, subDays, subMonths, parseISO } from 'date-fns';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import { isAfter } from 'date-fns';

import { TabHeader } from 'src/components/TabHeader/TabHeader';
import { Modal } from 'src/components/Modal/Modal';
import { PageSidebar } from 'src/components/PageSidebar/PageSidebar';
import { Checkbox } from 'src/components/Checkbox';
import { LogView } from 'src/components/LogView/LogView';
import { computeStatusGrid, getHoursAndMinutes } from 'src/components/LogView/utils';

import { HosEditLog } from 'src/features/hosLogs/hosEditLog/HosEditLog';
import { Page } from 'src/features/layout/Page';
import { OffDutyArrayInterface } from 'src/features/hosLogs/types';
import { HosException } from 'src/features/hosLogs/hosException/HosException';
import { getUserId } from 'src/features/profile/store';

import { isDesktop } from 'src/utils/devices';
import { ELD_DRIVER_STATUS } from 'src/constants/ELDConstants';

import {
  multiHosCertifyDaysCount,
  multiHosCertifyOffDutyDaysCount,
  fetchCalendarDotsLogsCount,
} from 'src/configs/gui-config';

import {
  getTimelineData,
  getEditingDutyStatus,
  getHosLogTimeZone,
  getHosLogs,
  getExceptions,
  getPendingExceptions,
  getIsAddingException,
  getIsInsertingDutyStatus,
  getActiveViolationId,
  fetchCalendarHosLogsAction,
  getCalendarHosLogs,
  getPendingEdits,
  getHosDaily,
  getUnsavedHOSLogEditsPresent,
  getUnsavedHOSExceptionsEditsPresent,
  getHosLogNormalized,
} from '../store';

import {
  certifyHosLogAction,
  fetchHosLog,
  fetchHosLogs,
  fetchUserLocationsAction,
  saveExceptionLog,
  setIsAddingException,
  setInsertingDutyStatus,
  setEditingStatus,
  multiplyCertifyHosLogsAction,
  setUnsavedHOSLogEditsPresent,
  setUnsavedHOSExceptionsEditsPresent,
} from '../store/hosLogStore';

import { HosEventsTab } from '../tabs/HosEventsTab';
import { FORM_MODE, supportTimeISO } from '../hosEditLog/utils';
import { HosFormsTab } from '../tabs/HosFormsTab';
import { HosDailyTab } from '../tabs/HosDailyTab';
import { LogHeader } from './components';
import { getHighlightedDays } from 'src/features/hosLogs/store/utils';
import { getDateWithoutTimezone } from 'src/utils/dateUtils';

// Utils
import { getDate6MonthsBack } from 'src/utils/dateUtils';

// Styles
import './hosLog.scss';
import 'react-datepicker/dist/react-datepicker.css';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { HOS_EDIT_ALLOW_ODOMETER_EDITS } from 'src/constants/featureFlags';

enum VIEW {
  EVENTS,
  FORMS,
  DAILY,
}

export const HosLog: React.FC = () => {
  const dispatch = useDispatch();

  const [currentView, setCurrentView] = useState(VIEW.EVENTS);
  const [openModal, setOpenModal] = useState(false);

  const activeViolationId = useSelector(getActiveViolationId);
  const insertDutyStatus = useSelector(getIsInsertingDutyStatus);
  const isAddingException = useSelector(getIsAddingException);

  const timelineData = useSelector(getTimelineData);
  const selectedHosLog = useSelector(getHosLogNormalized);

  const selectedEditingStatus = useSelector(getEditingDutyStatus);

  const exceptions = useSelector(getExceptions);
  const pendingExceptions = useSelector(getPendingExceptions);

  const employeeId = useSelector(getUserId);

  const hosLogs = useSelector(getHosLogs);

  const timeZone = useSelector(getHosLogTimeZone) || 'America/Chicago';

  const calendarHosLogs = useSelector(getCalendarHosLogs);

  const hosDaily = useSelector(getHosDaily);

  const routeMatch = useRouteMatch<{ date: string }>();
  const dateParam = routeMatch.params.date;

  const pendingEdits = useSelector(getPendingEdits);
  const isPendingEdits = useMemo(() => Object.keys(pendingEdits.timelineData).length, [
    pendingEdits,
  ]);

  const allowOdometerEdits = useFlags()[HOS_EDIT_ALLOW_ODOMETER_EDITS];

  const dispatchFetchHosLog = useCallback((): void => {
    if (employeeId) {
      dispatch(fetchUserLocationsAction(`${employeeId}:${dateParam}`));
      dispatch(
        fetchHosLog({
          logId: `${employeeId}:${dateParam}`,
        }),
      );
    }
  }, [dispatch, employeeId, dateParam]);

  const dateObj = useMemo(() => moment(dateParam).toDate(), [dateParam]);
  const logItemDate = useMemo(() => format(dateObj, 'EEE - MMM dd, yyyy'), [dateObj]);

  const isSameDay = useMemo(() => moment(dateObj).isSame(moment(), 'day'), [dateObj]);
  const history = useHistory();
  const location = useLocation();

  const changeView = useCallback(
    (view: VIEW) => (): void => {
      setCurrentView(view);
    },
    [],
  );

  useEffect(() => {
    const dateString = location.pathname.split('/')[2];

    if (isAfter(getDate6MonthsBack(), parseISO(dateString))) {
      history.push('/hos-log');
      toastr.error('Error', 'Log Not Available.');
    }
  }, []);

  useEffect(() => {
    dispatchFetchHosLog();
  }, [dispatchFetchHosLog, dateObj]);

  useEffect(() => {
    dispatch(setIsAddingException(false));
    dispatch(setInsertingDutyStatus(false));
    dispatch(setEditingStatus(undefined));
    changeView(VIEW.EVENTS)();
  }, [hosDaily]);

  const handleSaveEdits = useCallback((): void => {
    dispatch(
      saveExceptionLog({
        employeeId,
        exceptionDate: dateParam,
      }),
    );
  }, [dispatch, employeeId, dateParam]);

  const { statusGrid, statusTimes, violationsObject } = computeStatusGrid(
    isPendingEdits ? pendingEdits.timelineData : timelineData,
    isSameDay,
    timeZone,
  );

  const unsavedHOSLogDutyChangesPresent = useSelector(getUnsavedHOSLogEditsPresent);
  const unsavedHOSExceptionChangesPresent = useSelector(getUnsavedHOSExceptionsEditsPresent);
  const [showUnsavedLogDutyEditModal, setShowUnsavedLogDutyEditModal] = useState(false);
  const [showUnsavedDutyExceptionEditModal, setShowUnsavedDutyExceptionEditModal] = useState(false);
  const [destinationPageOnDiscard, setDestinationPageOnDiscard] = useState('#');

  useEffect(() => {
    const showHideModal = () => {
      if (unsavedHOSLogDutyChangesPresent) {
        setShowUnsavedLogDutyEditModal(true);
        return;
      }
      if (unsavedHOSExceptionChangesPresent) {
        setShowUnsavedDutyExceptionEditModal(true);
        return;
      }
    };
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', showHideModal);
    return () => {
      window.removeEventListener('popstate', showHideModal);
    };
  }, [unsavedHOSLogDutyChangesPresent, unsavedHOSExceptionChangesPresent]);

  const [offDutyArray, setOffDutyArray] = useState<Array<OffDutyArrayInterface>>([]);
  const showOffDutyDaysList = useMemo(() => offDutyArray.length > 0, [offDutyArray]);
  const offDutyCheckedCheckboxes = useMemo(
    () => offDutyArray.filter(item => item.active).length > 0,
    [offDutyArray],
  );

  useEffect(() => setOffDutyArray([]), [selectedHosLog]);

  const certifyHosLogActionHandler = useCallback(() => {
    dispatch(
      certifyHosLogAction({
        logDate: selectedHosLog.logDate,
        logId: employeeId,
      }),
    );
    setOpenModal(false);
  }, [dispatch, employeeId, selectedHosLog]);

  const onCertifyLogHandler = useCallback(() => {
    if (statusTimes && statusTimes.off === '24:00') {
      const offDutyArrayData = [];
      for (let i = 0; i < hosLogs.length; i++) {
        const item = hosLogs[i];
        let offDutyTime = item.timelineData.statusTimes[ELD_DRIVER_STATUS.OFF];
        offDutyTime = getHoursAndMinutes(offDutyTime);
        if (!item.certifiedStatus && offDutyTime === '24:00') {
          offDutyArrayData.push({
            logDate: item.date,
            formatLogDate: format(getDateWithoutTimezone(item.date), 'EEE - MMM. dd, yyyy'),
            active: false,
          });
        } else {
          break;
        }
      }
      if (offDutyArrayData.length >= multiHosCertifyOffDutyDaysCount) {
        setOffDutyArray(offDutyArrayData);
      } else {
        certifyHosLogActionHandler();
      }
    } else {
      certifyHosLogActionHandler();
    }
  }, [statusTimes, hosLogs, certifyHosLogActionHandler]);

  const closeDialogHandler = (): void => {
    setOpenModal(false);
    setOffDutyArray([]);
  };

  const onCertifyLogsHandler = useCallback(() => {
    const selectedOffDutyLogs = offDutyArray.filter(item => item.active);
    if (selectedOffDutyLogs.length > 0) {
      const refreshCurrentDay = selectedOffDutyLogs.find(
        item => item.logDate === selectedHosLog.logDate,
      );
      const logIds = selectedOffDutyLogs.map(item => `${employeeId}:${item.logDate}`);
      dispatch(
        multiplyCertifyHosLogsAction({
          employeeId: employeeId,
          logDate: selectedHosLog.logDate,
          updateDay: !!refreshCurrentDay,
          logIds: {
            logIds,
          },
        }),
      );
    }
    closeDialogHandler();
  }, [dispatch, offDutyArray, employeeId, selectedHosLog]);

  const onChangeCertifyCheckbox = useCallback(
    event => {
      const changedItemIndex = offDutyArray.findIndex((item: any) => {
        return item.logDate === event.target.name;
      });
      const offDutyArraySlice = offDutyArray.slice();
      offDutyArraySlice[changedItemIndex].active = !offDutyArraySlice[changedItemIndex].active;
      setOffDutyArray(offDutyArraySlice);
    },
    [offDutyArray],
  );

  const onSelectAllOffLogsHandler = useCallback(() => {
    const offDutyArraySlice = offDutyArray.slice();
    for (let i = 0; i < offDutyArraySlice.length; i++) {
      offDutyArraySlice[i].active = true;
    }
    setOffDutyArray(offDutyArraySlice);
  }, [offDutyArray]);

  const dispatchFetchHosLogs = useCallback((): void => {
    const { logDate } = selectedHosLog;
    const logDateWithoutTimezone = getDateWithoutTimezone(logDate);
    if (employeeId) {
      dispatch(
        fetchHosLogs({
          startDate: format(
            subDays(logDateWithoutTimezone, multiHosCertifyDaysCount),
            'yyyy-MM-dd',
          ),
          endDate: format(logDateWithoutTimezone, 'yyyy-MM-dd'),
          employeeIds: employeeId,
          count: 100,
          startIndex: 0,
        }),
      );
    }
  }, [dispatch, selectedHosLog, employeeId]);

  const handleShowCertifyConfirmation = useCallback(() => {
    if (unsavedHOSLogDutyChangesPresent) {
      setShowUnsavedLogDutyEditModal(true);
      return;
    }
    if (unsavedHOSExceptionChangesPresent) {
      setShowUnsavedDutyExceptionEditModal(true);
      return;
    }
    dispatchFetchHosLogs();
    setOpenModal(true);
  }, [dispatchFetchHosLogs, unsavedHOSLogDutyChangesPresent, unsavedHOSExceptionChangesPresent]);

  const onCloseInsertDutyStatus = useCallback(() => {
    if (unsavedHOSLogDutyChangesPresent) {
      setShowUnsavedLogDutyEditModal(true);
    } else {
      dispatch(setInsertingDutyStatus(false));
    }
  }, [dispatch, unsavedHOSLogDutyChangesPresent]);

  const onCloseEditingDutyStatus = useCallback(() => {
    if (unsavedHOSLogDutyChangesPresent) {
      setShowUnsavedLogDutyEditModal(true);
    } else {
      dispatch(setEditingStatus(undefined));
    }
  }, [dispatch, unsavedHOSLogDutyChangesPresent]);

  const onCloseException = () => {
    if (unsavedHOSExceptionChangesPresent) {
      setShowUnsavedDutyExceptionEditModal(true);
    } else {
      dispatch(setIsAddingException(false));
    }
  };

  const discardDutyEdits = () => {
    dispatch(setEditingStatus(undefined));
    dispatch(setInsertingDutyStatus(false));
    dispatch(setUnsavedHOSLogEditsPresent(false));
    setShowUnsavedLogDutyEditModal(false);
  };

  const discardExceptionEdits = () => {
    dispatch(setUnsavedHOSExceptionsEditsPresent(false));
    dispatch(setIsAddingException(false));
    setShowUnsavedDutyExceptionEditModal(false);
  };

  const goBackToHosHomePage = () => {
    setDestinationPageOnDiscard('/hos-log');
    if (unsavedHOSExceptionChangesPresent) {
      setShowUnsavedDutyExceptionEditModal(true);
      return;
    }
    if (unsavedHOSLogDutyChangesPresent) {
      setShowUnsavedLogDutyEditModal(true);
      return;
    }
    destinationPageOnDiscard.length > 0 && history.push(destinationPageOnDiscard);
  };

  const modalDescription = !showOffDutyDaysList
    ? 'I hereby certify that my data entries and my record of duty status for this 24-hour period are true and correct.'
    : `You have ${offDutyArray.length} consecutive days of logs that were also 24H OFF DUTY, would you like to certify these logs as well?`;

  const onChangeLogDay = (date: Date | [Date, Date] | null): void => {
    const newDate = date && format(new Date(date.toString()), 'yyyy-MM-dd');
    const url = `/hos-log/${newDate}`;

    if (unsavedHOSExceptionChangesPresent) {
      setDestinationPageOnDiscard(url);
      setShowUnsavedDutyExceptionEditModal(true);
      return;
    }
    if (unsavedHOSLogDutyChangesPresent) {
      setDestinationPageOnDiscard(url);
      setShowUnsavedLogDutyEditModal(true);
      return;
    }

    if (isAfter(getDate6MonthsBack(), date as Date)) {
      toastr.error('Error', 'Log Not Available.');
      return;
    }
    newDate && history.push(url);
  };

  const selectedDateForLogView = useMemo(
    () => moment.tz(selectedHosLog.logDate, selectedHosLog.timeZone),
    [selectedHosLog],
  );

  const switchTabView = (viewType: number): void => {
    if (unsavedHOSExceptionChangesPresent) {
      setShowUnsavedDutyExceptionEditModal(true);
      return;
    }
    if (unsavedHOSLogDutyChangesPresent) {
      setShowUnsavedLogDutyEditModal(true);
      return;
    }
    changeView(viewType)();
  };

  const highlightedDays = useMemo(() => getHighlightedDays(calendarHosLogs), [calendarHosLogs]);

  const hosContent = selectedEditingStatus ? (
    <HosEditLog
      formMode={FORM_MODE.EDIT}
      eventIndex={selectedEditingStatus.index}
      startTime={
        (supportTimeISO(selectedHosLog.hosGridObject) &&
          selectedHosLog.hosGridObject[selectedEditingStatus.index].timeISO) ||
        selectedEditingStatus.time
      }
      duration={selectedEditingStatus.duration}
      location={selectedEditingStatus.location}
      geoLocationData={selectedEditingStatus.geoLocationData || null}
      notes={selectedEditingStatus.notes}
      odometer={selectedEditingStatus.odometer}
      editedOdometer={selectedEditingStatus.editedOdometer}
      logStatus={selectedEditingStatus.status}
      onClose={onCloseEditingDutyStatus}
      allowOdometerEdits={allowOdometerEdits}
      showUnsavedDutyEditModal={showUnsavedLogDutyEditModal}
      discardDutyEdits={discardDutyEdits}
      hideDutyEditWarnModal={() => setShowUnsavedLogDutyEditModal(false)}
      destinationPage={destinationPageOnDiscard}
    />
  ) : insertDutyStatus ? (
    <HosEditLog
      formMode={FORM_MODE.ADD}
      eventIndex={-1}
      logStatus={ELD_DRIVER_STATUS.EMPTY}
      location=""
      geoLocationData={null}
      notes=""
      odometer={null}
      editedOdometer={null}
      startTime={selectedHosLog.logStartTimeISO || '00:00:00.000'}
      duration={0}
      onClose={onCloseInsertDutyStatus}
      allowOdometerEdits={allowOdometerEdits}
      showUnsavedDutyEditModal={showUnsavedLogDutyEditModal}
      discardDutyEdits={discardDutyEdits}
      hideDutyEditWarnModal={() => setShowUnsavedLogDutyEditModal(false)}
      destinationPage={destinationPageOnDiscard}
    />
  ) : isAddingException ? (
    <HosException
      logType="exceptions"
      logTitle="Exceptions"
      logStatus={0}
      location=""
      notes=""
      onClose={onCloseException}
      exceptions={exceptions}
      pendingExceptions={pendingExceptions}
      onSaveEdits={handleSaveEdits}
      showUnsavedExceptionModal={showUnsavedDutyExceptionEditModal}
      discardExceptionEdits={discardExceptionEdits}
      hideExceptionEditWarnModal={() => {
        setShowUnsavedDutyExceptionEditModal(false);
      }}
      destinationPage={destinationPageOnDiscard}
    />
  ) : (
    <HosEventsTab onSaveEdits={handleSaveEdits} allowOdometerEdits={allowOdometerEdits} />
  );

  return (
    <Page>
      {openModal ? (
        <Modal
          header={!showOffDutyDaysList ? logItemDate : null}
          description={modalDescription}
          confirmButton={!showOffDutyDaysList ? 'Certify Log' : 'Certify Selected Logs'}
          onCloseDialog={closeDialogHandler}
          onConfirmFunction={!showOffDutyDaysList ? onCertifyLogHandler : onCertifyLogsHandler}
          disabled={showOffDutyDaysList && !offDutyCheckedCheckboxes}
        >
          {showOffDutyDaysList ? (
            <>
              <button className="select-all-logs-button" onClick={onSelectAllOffLogsHandler}>
                select all 24H off logs
              </button>
              {offDutyArray.map((item: any) => {
                const title = (
                  <span>
                    <strong>24H OFF</strong> {item.formatLogDate}
                  </span>
                );
                return (
                  <Checkbox
                    key={item.logDate}
                    title={title}
                    name={item.logDate}
                    checked={item.active}
                    onChange={onChangeCertifyCheckbox}
                    typeButton
                  />
                );
              })}
            </>
          ) : (
            <p className="certify-log-warning">
              Please note that Logs are not editable once they have been certified.
            </p>
          )}
        </Modal>
      ) : null}

      <div className={classNames('hos-log', { 'is-desktop': isDesktop })}>
        {isDesktop && (
          <PageSidebar
            clickFn={() => {
              goBackToHosHomePage();
            }}
          >
            <DatePicker
              calendarClassName="hos-log__datepicker"
              selected={dateObj}
              onChange={onChangeLogDay}
              minDate={startOfMonth(subMonths(new Date(), 6))}
              maxDate={new Date()}
              inline
              highlightDates={highlightedDays.highlightedDays}
            />
          </PageSidebar>
        )}
        <div className="hos-log__main-content">
          <LogHeader
            logItemDate={logItemDate}
            selectedHosLog={selectedHosLog}
            onShowCertifyConfirmation={handleShowCertifyConfirmation}
          />
          <div className="hos-log-calendar">
            <LogView
              logStartTimeISO={selectedHosLog.logStartTimeISO}
              logLengthHours={selectedHosLog.logLengthHours}
              date={selectedDateForLogView}
              isOnSameDay={true}
              statusTimes={statusTimes}
              statusGrid={statusGrid}
              timeZone={selectedHosLog.timeZone}
              violations={violationsObject}
              activeViolationId={activeViolationId}
            />
          </div>

          <div className="hos-log-info">
            <>
              <div className="hos-log-tabs">
                <TabHeader
                  title="Events"
                  active={currentView === VIEW.EVENTS}
                  onClick={() => switchTabView(VIEW.EVENTS)}
                />
                <TabHeader
                  title="Forms"
                  active={currentView === VIEW.FORMS}
                  onClick={() => switchTabView(VIEW.FORMS)}
                />
                <TabHeader
                  title="Daily Header"
                  active={currentView === VIEW.DAILY}
                  onClick={() => switchTabView(VIEW.DAILY)}
                />
              </div>
              {currentView === VIEW.EVENTS && hosContent}
              {currentView === VIEW.FORMS && <HosFormsTab />}
              {currentView === VIEW.DAILY && <HosDailyTab />}
            </>
          </div>
        </div>
      </div>
    </Page>
  );
};
